import React, { useEffect, useRef } from 'react';
import * as am4charts from '@amcharts/amcharts4/charts';

import { IDeviceAnomalyDataUsageDataPoint } from '../../../../../services/core/deviceAnomaliesCharts';
import { StackedColumnsDateChart } from '../../../../../components/chart/stackedColumnsDateChart';
import { drawXAxisLabelsFromDateRange, italicAxisTitle } from '../../../../../components/chart/lib';
import { formatTooltipDay } from '../../../../../components/chart/tooltips';
import { useTranslation } from '@lib/useTypedTranslation';
import { drawYAxis } from '../../../../../lib/dataUsageChart';
import { getAnomalyBarClickHandler } from './lib';
import { getDataUsagePeriodLabels } from './dataUsageSeriesLabels';

const CHART_DAYS = 30;

// Mirrors deviceAnomaliesEventChart.tsx's identical constants/highlight plumbing - kept as its
// own self-contained copy here (rather than shared) since the two charts' data points have
// different shapes (6 mobile/wifi keys here vs 3 count keys there) and every other section/chart
// in this area (e.g. lowPowerSection.tsx vs eventTimelineSection.tsx) already favours a
// self-contained copy over a shared abstraction for this same kind of chart-instance plumbing.
export const SELECTED_COLUMN_OPACITY = 0.35;
export const DEFAULT_COLUMN_OPACITY = 1;

// Training: light/dark gray; Recent: light/dark blue; Anomaly: light/dark red
// Mobile = lighter shade, Wifi = darker shade (matches core performance convention)
const TRAINING_MOBILE_COLOUR = '#c8c8c8';
const TRAINING_WIFI_COLOUR = '#808080';
const RECENT_MOBILE_COLOUR = '#49a1a9';
const RECENT_WIFI_COLOUR = '#2c678c';
const ANOMALY_MOBILE_COLOUR = '#e87c7c';
const ANOMALY_WIFI_COLOUR = '#c0392b';

interface ITransformedDataPoint {
  date: number,
  trainingMobile: number | null,
  trainingWifi: number | null,
  recentMobile: number | null,
  recentWifi: number | null,
  anomalyMobile: number | null,
  anomalyWifi: number | null
}

function transformDataUsage(data: IDeviceAnomalyDataUsageDataPoint[]): ITransformedDataPoint[] {
  return data.map((point) => ({
    date: Number(point.date),
    trainingMobile: point.period === 'training' ? point.mobile : null,
    trainingWifi: point.period === 'training' ? point.wifi : null,
    recentMobile: point.period === 'evaluation' && !point.isAnomaly ? point.mobile : null,
    recentWifi: point.period === 'evaluation' && !point.isAnomaly ? point.wifi : null,
    anomalyMobile: point.period === 'evaluation' && point.isAnomaly ? point.mobile : null,
    anomalyWifi: point.period === 'evaluation' && point.isAnomaly ? point.wifi : null
  }));
}

// Unlike deviceAnomaliesEventChart.tsx's findColumnForDate, a day's mobile and wifi values are
// stacked as two separate columns sharing the same date (see transformDataUsage), so every matching
// column has to be collected for the whole stacked bar to be highlighted.
function findColumnsForDate(chart: am4charts.XYChart, date: number): am4charts.Column[] {
  const found: am4charts.Column[] = [];
  chart.series.each((series) => {
    (series as am4charts.ColumnSeries).columns.each((column) => {
      const dataContext = column.dataItem?.dataContext as ITransformedDataPoint | undefined;
      if (dataContext && Number(dataContext.date) === date) {
        found.push(column);
      }
    });
  });
  return found;
}

// Mirrors deviceAnomaliesEventChart.tsx's identical setColumnOpacity - see its own comment for why
// this updates defaultState.properties rather than assigning fillOpacity directly.
function setColumnOpacity(column: am4charts.Column, opacity: number) {
  column.defaultState.properties.fillOpacity = opacity;
  column.setState('default');
}

// Mirrors deviceAnomaliesEventChart.tsx's identical applySelectedDateHighlight - see its own
// comment for the full rationale (including why the "chart not ready yet" guard is unreachable
// through an actual render, but still exercised directly by this file's own unit test).
export function applySelectedDateHighlight(
  chart: am4charts.XYChart | null,
  highlightedColumnsRef: React.MutableRefObject<am4charts.Column[]>,
  selectedDate: number | undefined
) {
  if (!chart) {
    return;
  }
  highlightedColumnsRef.current.forEach((column) => setColumnOpacity(column, DEFAULT_COLUMN_OPACITY));
  highlightedColumnsRef.current = [];
  if (selectedDate) {
    const columns = findColumnsForDate(chart, selectedDate);
    columns.forEach((column) => setColumnOpacity(column, SELECTED_COLUMN_OPACITY));
    highlightedColumnsRef.current = columns;
  }
}

interface IProps {
  data: IDeviceAnomalyDataUsageDataPoint[],
  title: string,
  cypressId: string,
  timeZone: string,
  onMount: (chart: am4charts.XYChart) => am4charts.XYChart,
  onDayClick?: (date: number) => void,
  selectedDate?: number
}

export function DeviceAnomaliesDataUsage(props: IProps) {
  const { t } = useTranslation('anomalyDetectionBeta');
  const { t: tBase } = useTranslation(['translation', 'performance']);
  const transformed = transformDataUsage(props.data);
  const dateRange = transformed.length ? {
    from: Math.min(...transformed.map((point) => point.date)),
    to: Math.max(...transformed.map((point) => point.date))
  } : undefined;

  const onBarClick = getAnomalyBarClickHandler<ITransformedDataPoint>(
    (dataPoint) => Boolean(
      dataPoint.trainingMobile || dataPoint.trainingWifi ||
      dataPoint.recentMobile || dataPoint.recentWifi ||
      dataPoint.anomalyMobile || dataPoint.anomalyWifi
    ),
    props.onDayClick
  );

  const chartRef = useRef<am4charts.XYChart>(null);
  const highlightedColumnsRef = useRef<am4charts.Column[]>([]);

  useEffect(() => {
    const chart = chartRef.current;
    applySelectedDateHighlight(chart, highlightedColumnsRef, props.selectedDate);
    const disposer = chart?.events.on('validated', () => {
      applySelectedDateHighlight(chart, highlightedColumnsRef, props.selectedDate);
    });
    const seriesDisposers: { dispose: () => void }[] = [];
    chart?.series.each((series) => {
      seriesDisposers.push(series.events.on('validated', () => {
        applySelectedDateHighlight(chart, highlightedColumnsRef, props.selectedDate);
      }));
    });
    return () => {
      disposer?.dispose();
      seriesDisposers.forEach((seriesDisposer) => seriesDisposer.dispose());
    };
  }, [props.selectedDate]);

  // The legend is intentionally simpler than the chart itself: each period (training/recent/
  // anomaly) renders two stacked series (mobile + wifi), but should only contribute a single
  // combined legend row (e.g. "Training Data Usage") rather than two near-duplicate ones. Giving
  // both series in a pair the same legendLabel, and hiding one of the two from the legend via
  // hideFromLegend, achieves that while keeping both bars (and both tooltip rows, which still key
  // off the more specific per-series description) intact.
  // Every label is a complete, per-locale phrase (see getDataUsagePeriodLabels) rather than a
  // period word joined to a usage phrase here, which only reads correctly in English word order.
  const training = getDataUsagePeriodLabels('training', t, tBase);
  const recent = getDataUsagePeriodLabels('recent', t, tBase);
  const anomaly = getDataUsagePeriodLabels('anomaly', t, tBase);

  return (
    <StackedColumnsDateChart<any>
      data={transformed}
      series={[
        {
          visible: true,
          dataKey: 'trainingMobile',
          description: training.mobile,
          legendLabel: training.legend,
          colour: TRAINING_MOBILE_COLOUR
        },
        {
          visible: true,
          dataKey: 'trainingWifi',
          description: training.wifi,
          legendLabel: training.legend,
          hideFromLegend: true,
          colour: TRAINING_WIFI_COLOUR
        },
        {
          visible: true,
          dataKey: 'recentMobile',
          description: recent.mobile,
          legendLabel: recent.legend,
          colour: RECENT_MOBILE_COLOUR
        },
        {
          visible: true,
          dataKey: 'recentWifi',
          description: recent.wifi,
          legendLabel: recent.legend,
          hideFromLegend: true,
          colour: RECENT_WIFI_COLOUR
        },
        {
          visible: true,
          dataKey: 'anomalyMobile',
          description: anomaly.mobile,
          legendLabel: anomaly.legend,
          colour: ANOMALY_MOBILE_COLOUR
        },
        {
          visible: true,
          dataKey: 'anomalyWifi',
          description: anomaly.wifi,
          legendLabel: anomaly.legend,
          hideFromLegend: true,
          colour: ANOMALY_WIFI_COLOUR
        }
      ]}
      title={props.title}
      days={CHART_DAYS}
      cypressId={props.cypressId}
      hasLegend={true}
      xAxisType={'date'}
      onBarClick={onBarClick}
      drawXAxis={(chart, xAxis) => {
        chartRef.current = chart;
        if (dateRange) {
          drawXAxisLabelsFromDateRange(xAxis as am4charts.DateAxis, dateRange);
        }
        xAxis.title.text = italicAxisTitle(`${tBase('TIMEZONE', { ns: 'translation' })} (${props.timeZone})`);
      }}
      drawYAxis={(chart, axis, enabledSeries) => drawYAxis(chart.data, axis, enabledSeries)}
      formatTooltipDate={formatTooltipDay}
      amchartsYAxisNumberFormat="#.00b"
      onMount={props.onMount}
      useAxisTooltip
      hideNullSeriesInTooltip
    />
  );
}
